import { StrictMode, type ComponentType } from 'react';
import { createRoot } from 'react-dom/client';
import './theme/tokens.css';
import './theme/global.css';
import './theme/app.css';
import { applyStoredTextSize } from './lib/textSize';

applyStoredTextSize();

// After a deploy, a tab still on the old build asks for chunks that no longer
// exist: reload it onto the new build. At most once every 30 s, or once per
// page load when session storage is blocked, so a broken build can't loop fast.
const RELOAD_KEY = 'go.preloadErrorReloadAt';
let reloadedThisPage = false;
window.addEventListener('vite:preloadError', () => {
  if (reloadedThisPage) return;
  let last = 0;
  try { last = Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0; } catch { last = 0; }
  if (Date.now() - last < 30_000) return;
  try { window.sessionStorage.setItem(RELOAD_KEY, String(Date.now())); } catch { /* blocked: the page-load guard still applies */ }
  reloadedThisPage = true;
  window.location.reload();
});

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root element');

// Public survey links and visitor-facing form pages load only their own renderer; everything else loads the full app.
const path = window.location.pathname;
const entry: Promise<ComponentType> = path.startsWith('/survey/')
  ? import('./SurveyApp').then((m) => m.SurveyApp)
  : /^\/(f|r|p)\/[^/]+\/?$/.test(path)
    ? import('./FormsApp').then((m) => m.FormsApp)
    : import('./App').then((m) => m.App);

// The signed-in app can be added to a phone's home screen and get "Call on my phone" notifications.
if ('serviceWorker' in navigator && !path.startsWith('/survey/') && !/^\/(f|r|p)\/[^/]+\/?$/.test(path)) {
  navigator.serviceWorker.register('/sw.js').catch(() => undefined);
}

void entry.then((Root) => {
  createRoot(root).render(
    <StrictMode>
      <Root />
    </StrictMode>,
  );
});
